import { useRef, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_IMPORT_BYTES,
  OWNER_BALANCE_COLUMNS,
  PAID_TO_COLUMNS,
  UNPRESENTED_COLUMNS,
  canCutOver,
  formatNzDate,
  parseAmount,
  toCsv,
  type CutoverColumn,
} from '@promanage/core';
import { Amount, Button, Chip, Field, Input, Panel, PanelHeader, Select } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { LoadFailed } from '../components/QueryState';
import { SettingsNav } from '../features/settings/SettingsNav';
import { useSession } from '../features/auth/session';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/settings/opening-balances')({
  component: OpeningBalancesPage,
});

/**
 * Cutover — bringing the agency's money across from the system it is leaving.
 *
 * Balances, not history: each owner's money per property, each tenancy's paid-to date, and
 * whatever the bank had not yet shown. Nothing posts until three independent figures agree to the
 * cent — the bank with its unpresented items, the owner balances in the file, and the total the
 * old system's own reconciliation report states. See `cutover.ts` in core.
 *
 * A LED PAGE, like the import. CLAUDE.md §1 says the operator is an expert and should not be held
 * by the hand; this is the exception it implies, because it is done once in an agency's life by
 * somebody who has never seen it, on the day every balance they hold is at stake.
 */
function OpeningBalancesPage() {
  const session = useSession();
  const allowed = canCutOver(session?.roles ?? []);

  if (!allowed) {
    return (
      <Frame>
        <Panel className="p-6">
          <h2 className="text-[1.125rem] font-bold">
            Bringing balances across needs a trust administrator
          </h2>
          <p className="mt-1 max-w-prose text-[0.875rem] text-ink-muted">
            It posts the opening balance of every owner in the trust account at once, and it can be
            done only once. Ask a trust administrator to run it.
          </p>
        </Panel>
      </Frame>
    );
  }

  return <Cutover />;
}

type CsvFile = { name: string; csv: string };

function Cutover() {
  const queryClient = useQueryClient();
  const status = useQuery(trpc.cutover.status.queryOptions());

  const [sourceSystem, setSourceSystem] = useState('');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [cutoverOn, setCutoverOn] = useState('');
  const [statement, setStatement] = useState('');
  const [oldTotal, setOldTotal] = useState('');
  const [owners, setOwners] = useState<CsvFile | null>(null);
  const [tenancies, setTenancies] = useState<CsvFile | null>(null);
  const [unpresented, setUnpresented] = useState<CsvFile | null>(null);

  const preview = useMutation(
    trpc.cutover.preview.mutationOptions({ meta: { errorTitle: 'Could not check the cutover' } }),
  );
  const commit = useMutation(
    trpc.cutover.commit.mutationOptions({
      meta: { errorTitle: 'Nothing was brought across' },
      onSuccess() {
        // Every balance, every arrears figure and the reconciliation are new. Blunt and correct.
        void queryClient.invalidateQueries();
      },
    }),
  );

  if (status.isPending) {
    return (
      <Frame>
        <p className="text-[0.8125rem] text-ink-muted">Loading…</p>
      </Frame>
    );
  }
  if (status.error) {
    return (
      <Frame>
        <LoadFailed retry={() => void status.refetch()} retrying={status.isFetching} />
      </Frame>
    );
  }

  const data = status.data;

  if (data.done) {
    return (
      <Frame>
        <Panel className="p-6">
          <div className="flex flex-wrap items-center gap-2">
            <Chip tone="positive">Brought across</Chip>
            <h2 className="text-[1.125rem] font-bold">
              Opening balances at {formatNzDate(data.done.cutoverOn)}, from {data.done.sourceSystem}
            </h2>
          </div>
          <dl className="mt-4 grid max-w-xl grid-cols-[1fr_auto] gap-x-6 gap-y-1.5 text-[0.8125rem]">
            <dt className="text-ink-muted">Bank balance</dt>
            <dd>
              <Amount value={BigInt(data.done.statementBalanceCents)} />
            </dd>
            <dt className="text-ink-muted">Unpresented at cutover ({data.done.unpresentedRows})</dt>
            <dd>
              <Amount value={BigInt(data.done.unpresentedTotalCents)} />
            </dd>
            <dt className="text-ink-muted">Owner balances ({data.done.ownerRows})</dt>
            <dd>
              <Amount value={BigInt(data.done.ownerTotalCents)} className="font-semibold" />
            </dd>
            <dt className="text-ink-muted">Tenancies with a paid-to date</dt>
            <dd className="text-right tabular-nums">{data.done.tenancyRows}</dd>
            <dt className="text-ink-muted">Opening transaction</dt>
            <dd className="font-mono tabular-nums">{data.done.openingTransactionRef}</dd>
          </dl>
          <p className="mt-4 max-w-prose text-[0.8125rem] text-ink-muted">
            This happens once. A correction to an opening balance is a journal against the opening
            transaction, like any other correction.
          </p>
          {/* The step after this one: the same comparison, asked again a month on. */}
          <Link to="/settings/parallel-run" className="mt-3 inline-block">
            <Button size="sm" variant="secondary">
              Run a parallel comparison
            </Button>
          </Link>
        </Panel>
      </Frame>
    );
  }

  if (!data.ledgerEmpty) {
    return (
      <Frame>
        <Panel className="p-6">
          <h2 className="text-[1.125rem] font-bold">The ledger already has transactions in it</h2>
          <p className="mt-1 max-w-prose text-[0.875rem] text-ink-muted">
            Opening balances go into an empty ledger — they are the first thing in the books, or a
            receipt posted before them would be counted twice. Bring balances across before
            receipting anything here.
          </p>
        </Panel>
      </Frame>
    );
  }

  const account = data.trustAccounts.find((a) => a.id === accountId) ?? data.trustAccounts[0];
  const statementCents = cents(statement);
  const oldTotalCents = cents(oldTotal);

  const missing = [
    ...(sourceSystem.trim() === '' ? ['the system you are leaving'] : []),
    ...(account ? [] : ['a trust bank account (set one up first)']),
    ...(cutoverOn === '' ? ['the cutover date'] : []),
    ...(statementCents === null ? ['the bank balance'] : []),
    ...(oldTotalCents === null ? ["the old system's total"] : []),
    ...(owners ? [] : ['the owner balances file']),
    ...(tenancies ? [] : ['the paid-to dates file']),
  ];

  const request =
    missing.length === 0 &&
    account &&
    owners &&
    tenancies &&
    statementCents !== null &&
    oldTotalCents !== null
      ? {
          ledgerAccountId: account.id,
          cutoverOn,
          statementBalanceCents: statementCents.toString(),
          oldSystemTotalCents: oldTotalCents.toString(),
          sourceSystem: sourceSystem.trim(),
          ownersCsv: owners.csv,
          tenanciesCsv: tenancies.csv,
          unpresentedCsv: unpresented?.csv ?? null,
        }
      : null;

  /** Any edit makes the last check describe something else. */
  const changed =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      preview.reset();
      commit.reset();
    };

  const result = preview.data && !preview.isPending ? preview.data : null;

  return (
    <Frame>
      <div className="flex flex-col gap-4">
        {/* ── 1. The figures ─────────────────────────────────────────────────── */}
        <Panel className="p-0">
          <PanelHeader
            title="1. The cutover date and its two figures"
            subtitle="Choose the last day of a month the old system has reconciled and closed. Both figures are for that day."
          />
          <div className="grid gap-4 border-t border-rule px-5 py-4 sm:grid-cols-2">
            <Field
              label="The system you are leaving"
              hint="For the audit record, e.g. the product name"
            >
              {(props) => (
                <Input
                  {...props}
                  value={sourceSystem}
                  maxLength={80}
                  onChange={(e) => changed(setSourceSystem)(e.target.value)}
                />
              )}
            </Field>
            <Field label="Trust bank account">
              {(props) => (
                <Select
                  {...props}
                  value={account?.id ?? ''}
                  onChange={(e) => changed(setAccountId)(e.target.value)}
                >
                  {data.trustAccounts.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Cutover date">
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  max={data.today}
                  value={cutoverOn}
                  onChange={(e) => changed(setCutoverOn)(e.target.value)}
                />
              )}
            </Field>
            <Field
              label="Bank balance at close of that day"
              hint="Off the bank statement, not the old system"
              error={statement !== '' && statementCents === null ? 'Not an amount' : undefined}
            >
              {(props) => (
                <Input
                  {...props}
                  inputMode="decimal"
                  value={statement}
                  onChange={(e) => changed(setStatement)(e.target.value)}
                />
              )}
            </Field>
            <Field
              label="Total owed to beneficiaries, per the old system"
              hint="From its trust account reconciliation report for that day"
              error={oldTotal !== '' && oldTotalCents === null ? 'Not an amount' : undefined}
            >
              {(props) => (
                <Input
                  {...props}
                  inputMode="decimal"
                  value={oldTotal}
                  onChange={(e) => changed(setOldTotal)(e.target.value)}
                />
              )}
            </Field>
          </div>
        </Panel>

        {/* ── 2. The files ───────────────────────────────────────────────────── */}
        <Panel className="p-0">
          <PanelHeader
            title="2. The three files"
            subtitle="Exported from the old system as CSV. Columns are found by their headings, so the old system's own names usually work."
          />
          <div className="grid gap-4 border-t border-rule px-5 py-4 lg:grid-cols-3">
            <FileSlot
              title="Owner balances"
              note="One row per owner per property, as the trial balance lists them. Leave the property empty for money held against no particular one."
              columns={OWNER_BALANCE_COLUMNS}
              example={['J Patel', '12 Kowhai St', '1250.00']}
              file={owners}
              onChoose={changed(setOwners)}
            />
            <FileSlot
              title="Paid-to dates"
              note="One row per tenancy running at the cutover. Rent is charged here from the day after. Arrears is optional, and is only compared."
              columns={PAID_TO_COLUMNS}
              example={['TE000003', '14/09/2026', '0.00']}
              file={tenancies}
              onChoose={changed(setTenancies)}
            />
            <FileSlot
              title="Unpresented items"
              optional
              note="What the old system recorded and the bank had not shown by the cutover. Payments as negative. Each names the owner it belongs to."
              columns={UNPRESENTED_COLUMNS}
              example={['30/09/2026', 'Rent deposit TE000003', '600.00', 'J Patel', '12 Kowhai St']}
              file={unpresented}
              onChoose={changed(setUnpresented)}
            />
          </div>
        </Panel>

        {/* ── 3. Prove, then bring across ────────────────────────────────────── */}
        <Panel className="p-5">
          <PanelHeader
            title="3. Prove it"
            subtitle="Reads every row, finds every owner, property and tenancy it names, and checks the three totals agree. Nothing is written."
            className="p-0 pb-3"
          />

          {missing.length > 0 ? (
            <p className="text-[0.8125rem] text-ink-muted">Still needed: {missing.join(', ')}.</p>
          ) : (
            <Button onClick={() => request && preview.mutate(request)} disabled={preview.isPending}>
              {preview.isPending ? 'Checking' : 'Check the cutover'}
            </Button>
          )}

          {result ? (
            <div className="mt-4 flex flex-col gap-4">
              {result.proof ? <Proof proof={result.proof} /> : null}

              {result.blockers.length > 0 ? (
                <IssueList
                  tone="danger"
                  title={`${result.blockers.length} thing${result.blockers.length === 1 ? '' : 's'} to fix first`}
                  issues={result.blockers}
                />
              ) : null}
              {result.warnings.length > 0 ? (
                <IssueList
                  tone="warn"
                  title="Worth checking — these do not stop the cutover"
                  issues={result.warnings}
                />
              ) : null}

              {result.tenancies.length > 0 ? <PaidTo lines={result.tenancies} /> : null}

              {result.endedBeforeCutover > 0 ? (
                <p className="text-[0.8125rem] text-ink-muted">
                  {result.endedBeforeCutover} tenanc{result.endedBeforeCutover === 1 ? 'y' : 'ies'}{' '}
                  ended before the cutover and will raise no rent here — their history stays in the
                  old system.
                </p>
              ) : null}

              {commit.data ? (
                <div className="rounded-lg bg-positive-soft px-4 py-3 ring-1 ring-positive/25">
                  <p className="text-[0.9375rem] font-bold text-positive">
                    Brought across as {commit.data.openingTransactionRef}.
                  </p>
                  <p className="mt-1 text-[0.8125rem] text-ink-muted">
                    {commit.data.transactionsPosted} transaction
                    {commit.data.transactionsPosted === 1 ? '' : 's'} posted,{' '}
                    {commit.data.chargesRaised} rent charge
                    {commit.data.chargesRaised === 1 ? '' : 's'} raised from the paid-to dates, and
                    the cutover day reconciled as {commit.data.reconciliationRef}.
                  </p>
                </div>
              ) : result.ready && request ? (
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    size="lg"
                    onClick={() => commit.mutate(request)}
                    disabled={commit.isPending}
                  >
                    {commit.isPending ? 'Bringing across' : 'Bring the balances across'}
                  </Button>
                  <p className="max-w-prose text-[0.75rem] text-ink-subtle">
                    Posts the opening balances, starts rent from each paid-to date and reconciles
                    the cutover day — all of it, or none. It can be done once, and is recorded with
                    who ran it.
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}
        </Panel>
      </div>
    </Frame>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Opening balances"
        subtitle="Bring each owner's balance and each tenancy's paid-to date across from the system you are leaving"
        // No tab of its own — it is the last step of the Import page, and goes back there. The
        // same shape as "All automations" on an automation: a secondary button in the bar.
        actions={
          <Link to="/settings/import">
            <Button size="sm" variant="secondary">
              Back to import
            </Button>
          </Link>
        }
      />
      <SettingsNav />
      {children}
    </div>
  );
}

// ── The proof ────────────────────────────────────────────────────────────────

interface ProofWire {
  statementBalanceCents: string;
  unpresentedTotalCents: string;
  adjustedBankCents: string;
  ownerTotalCents: string;
  oldSystemTotalCents: string;
  bankDifferenceCents: string;
  oldSystemDifferenceCents: string;
  balanced: boolean;
}

/** The three figures, in the order an accountant reads them, with each difference beside it. */
function Proof({ proof }: { proof: ProofWire }) {
  const bankOut = BigInt(proof.bankDifferenceCents);
  const oldOut = BigInt(proof.oldSystemDifferenceCents);
  return (
    <div className="max-w-xl rounded-lg bg-surface-sunk px-4 py-3">
      <div className="flex items-center gap-2">
        {proof.balanced ? (
          <Chip tone="positive">Proved</Chip>
        ) : (
          <Chip tone="danger">Does not agree</Chip>
        )}
        <span className="text-[0.8125rem] text-ink-muted">
          Bank, file and old system, to the cent
        </span>
      </div>
      <dl className="mt-3 grid grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-[0.8125rem]">
        <dt className="text-ink-muted">Bank balance</dt>
        <dd>
          <Amount value={BigInt(proof.statementBalanceCents)} />
        </dd>
        <dt className="text-ink-muted">Add: unpresented items</dt>
        <dd>
          <Amount value={BigInt(proof.unpresentedTotalCents)} />
        </dd>
        <dt className="border-t border-rule pt-1 font-medium">Bank, adjusted</dt>
        <dd className="border-t border-rule pt-1">
          <Amount value={BigInt(proof.adjustedBankCents)} className="font-semibold" />
        </dd>
        <dt className="font-medium">Owner balances in the file</dt>
        <dd>
          <Amount value={BigInt(proof.ownerTotalCents)} className="font-semibold" />
        </dd>
        <dt className="font-medium">Old system&rsquo;s stated total</dt>
        <dd>
          <Amount value={BigInt(proof.oldSystemTotalCents)} className="font-semibold" />
        </dd>
        {bankOut !== 0n ? (
          <>
            <dt className="text-danger">Bank against file</dt>
            <dd>
              <Amount value={bankOut} tone="auto" />
            </dd>
          </>
        ) : null}
        {oldOut !== 0n ? (
          <>
            <dt className="text-danger">File against old system</dt>
            <dd>
              <Amount value={oldOut} tone="auto" />
            </dd>
          </>
        ) : null}
      </dl>
    </div>
  );
}

function IssueList({
  tone,
  title,
  issues,
}: {
  tone: 'danger' | 'warn';
  title: string;
  issues: readonly { sentence: string }[];
}) {
  return (
    <div>
      <p
        className={`text-[0.8125rem] font-semibold ${tone === 'danger' ? 'text-danger' : 'text-warn'}`}
      >
        {title}
      </p>
      <ul className="mt-1 flex list-disc flex-col gap-1 pl-5 text-[0.8125rem] text-ink-muted">
        {issues.map((issue, index) => (
          <li key={`${String(index)}-${issue.sentence}`}>{issue.sentence}</li>
        ))}
      </ul>
    </div>
  );
}

function PaidTo({
  lines,
}: {
  lines: readonly {
    tenancyRef: string;
    address: string;
    paidTo: string;
    chargesFrom: string | null;
    arrearsAtCutoverCents: string;
    oldArrearsCents: string | null;
  }[];
}) {
  return (
    <div className="overflow-x-auto">
      <p className="text-[0.8125rem] font-semibold">What each tenancy will open at</p>
      <table className="mt-1 w-full min-w-[36rem] text-[0.8125rem]">
        <thead>
          <tr className="text-left text-[0.6875rem] tracking-wide text-ink-subtle uppercase">
            <th className="py-1 pr-3 font-semibold">Tenancy</th>
            <th className="py-1 pr-3 font-semibold">Paid to</th>
            <th className="py-1 pr-3 font-semibold">Rent charged from</th>
            <th className="py-1 pr-3 text-right font-semibold">Owing at cutover</th>
            <th className="py-1 text-right font-semibold">Old system said</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.tenancyRef} className="border-t border-rule">
              <td className="py-1 pr-3">
                <span className="font-mono tabular-nums">{line.tenancyRef}</span>{' '}
                <span className="text-ink-muted">{line.address}</span>
              </td>
              <td className="py-1 pr-3 tabular-nums">{formatNzDate(line.paidTo)}</td>
              <td className="py-1 pr-3 tabular-nums">
                {line.chargesFrom ? formatNzDate(line.chargesFrom) : 'Its start'}
              </td>
              <td className="py-1 pr-3 text-right">
                <Amount value={BigInt(line.arrearsAtCutoverCents)} />
              </td>
              <td className="py-1 text-right">
                {line.oldArrearsCents === null ? (
                  <span className="text-ink-subtle">—</span>
                ) : (
                  <Amount value={BigInt(line.oldArrearsCents)} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── A file ───────────────────────────────────────────────────────────────────

function FileSlot({
  title,
  note,
  columns,
  example,
  file,
  onChoose,
  optional = false,
}: {
  title: string;
  note: string;
  columns: readonly CutoverColumn[];
  example: readonly string[];
  file: CsvFile | null;
  onChoose: (file: CsvFile | null) => void;
  optional?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const read = (chosen: File | undefined): void => {
    if (!chosen) return;
    setProblem(null);
    if (chosen.size > MAX_IMPORT_BYTES) {
      setProblem(
        `That file is ${Math.round(chosen.size / 1024)}KB; the limit is ${Math.round(MAX_IMPORT_BYTES / 1024)}KB.`,
      );
      return;
    }
    chosen
      .text()
      .then((csv) => onChoose({ name: chosen.name, csv }))
      .catch(() => setProblem('That file could not be read. Save it again as CSV and retry.'));
  };

  /** A blank file with the headings and one example row, so the shape is never a guess. */
  const downloadTemplate = () => {
    const csv = toCsv([columns.map((column) => column.label), [...example]]);
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title.toLowerCase().replace(/\s+/g, '-')}-template.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-surface-sunk p-4">
      <p className="flex items-center gap-2 text-[0.9375rem] font-bold">
        {title}
        {optional ? (
          <span className="text-[0.75rem] font-normal text-ink-subtle">optional</span>
        ) : null}
      </p>
      <p className="text-[0.75rem] text-ink-muted">{note}</p>
      <p className="text-[0.75rem] text-ink-subtle">
        Columns:{' '}
        {columns
          .map((column) => `${column.label}${column.required ? '' : ' (optional)'}`)
          .join(', ')}
      </p>

      <input
        ref={input}
        type="file"
        accept=".csv,text/csv,text/plain"
        className="sr-only"
        onChange={(e) => read(e.target.files?.[0])}
      />
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {file ? <Chip tone="positive">{file.name}</Chip> : null}
        <Button size="sm" variant="secondary" onClick={() => input.current?.click()}>
          {file ? 'Choose another' : 'Choose a file'}
        </Button>
        {file && optional ? (
          <Button size="sm" variant="ghost" onClick={() => onChoose(null)}>
            Remove
          </Button>
        ) : null}
        <Button size="sm" variant="ghost" onClick={downloadTemplate}>
          Template
        </Button>
      </div>
      {problem ? (
        <p role="alert" className="text-[0.75rem] font-medium text-danger">
          {problem}
        </p>
      ) : null}
    </div>
  );
}

/** An amount as typed, into cents — or null while it is not one. */
function cents(value: string): bigint | null {
  if (value.trim() === '') return null;
  try {
    return parseAmount(value);
  } catch {
    return null;
  }
}
